/**
 * User Quota Management
 * The server counts registered users' analyses in quota_usage/{uid} and enforces
 * the monthly limits; this module only reads that record for display.
 */

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

const FRAMEWORK_LIMITS = {
  current: 10,
  advanced: 3,
};

function normalizeFrameworkType(frameworkType) {
  return frameworkType === 'advanced' ? 'advanced' : 'current';
}

function getFrameworkLimit(frameworkType) {
  const normalized = normalizeFrameworkType(frameworkType);
  return FRAMEWORK_LIMITS[normalized] ?? FRAMEWORK_LIMITS.current;
}

/**
 * Initialize user document on first sign-in
 * Includes retry logic and validation to ensure data is properly saved
 */
export async function initializeUserDocument(userId, email) {
  const userRef = doc(db, 'users', userId);

  try {
    const userDoc = await getDoc(userRef);

    // If document exists, check if all required fields are present
    if (userDoc.exists()) {
      const data = userDoc.data();

      // Validate that all critical fields exist and are not undefined
      const hasValidData =
        data.uid !== undefined &&
        data.email !== undefined &&
        data.registeredAt !== undefined &&
        data.lastResetDate !== undefined;

      if (hasValidData) {
        const updates = {};
        const legacyCount = data.analysisCount ?? 0;

        if (data.analysisCount === undefined) {
          updates.analysisCount = legacyCount;
        }
        if (data.analysisCountCurrent === undefined) {
          updates.analysisCountCurrent = legacyCount;
        }
        if (data.analysisCountAdvanced === undefined) {
          updates.analysisCountAdvanced = 0;
        }

        if (Object.keys(updates).length > 0) {
          await updateDoc(userRef, updates);
        }

        console.log('✅ User document already initialized correctly');
        return userRef;
      }

      console.warn('⚠️ User document exists but has invalid/undefined fields. Re-initializing...');
      // Fall through to re-initialize
    }

    // Create or fix the document
    console.log('Initializing user document for:', email);

    await setDoc(userRef, {
      uid: userId,
      email: email,
      registeredAt: serverTimestamp(),
      analysisCount: 0,
      analysisCountCurrent: 0,
      analysisCountAdvanced: 0,
      lastAnalysisDate: serverTimestamp(),
      lastResetDate: serverTimestamp()
    }, { merge: false }); // Use merge: false to overwrite completely

    // Verify the write succeeded by reading it back
    const verifyDoc = await getDoc(userRef);

    if (!verifyDoc.exists()) {
      throw new Error('Failed to create user document - document does not exist after write');
    }

    const verifyData = verifyDoc.data();
    if (verifyData.analysisCount === undefined || verifyData.analysisCountCurrent === undefined) {
      throw new Error('Failed to create user document - analysis counts are undefined after write');
    }

    console.log('✅ User document initialized successfully:', verifyData);
    return userRef;

  } catch (error) {
    console.error('❌ Error initializing user document:', error);

    // If it's an offline error, provide helpful message
    if (error.code === 'unavailable' || error.message.includes('offline')) {
      console.error('⚠️ Firestore is offline. User document initialization will retry on next connection.');
    }

    throw error; // Re-throw so caller knows initialization failed
  }
}

/**
 * Save analysis result to user's history
 */
export async function saveAnalysisResult(userId, analysisData) {
  const analysisRef = doc(db, 'analyses', `${userId}_${Date.now()}`);
  
  await setDoc(analysisRef, {
    userId,
    timestamp: serverTimestamp(),
    companyName: analysisData.company_name || 'Unknown',
    reportYear: analysisData.report_year || null,
    overallScore: analysisData.overall_score || 0,
    categoryScores: analysisData.category_scores || {},
    frameworkType: analysisData.framework_type || analysisData.analysis_metadata?.framework_type || 'current',
    // Store only summary data, not full analysis (GDPR data minimization)
  });
}

/**
 * Get user's analysis history
 */
export async function getUserAnalyses(userId, limit = 10) {
  const { collection, query, where, orderBy, getDocs, limit: limitQuery } = await import('firebase/firestore');
  
  const analysesRef = collection(db, 'analyses');
  const q = query(
    analysesRef,
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limitQuery(limit)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Delete user's analysis
 */
export async function deleteAnalysis(analysisId) {
  const { deleteDoc, doc } = await import('firebase/firestore');
  const analysisRef = doc(db, 'analyses', analysisId);
  await deleteDoc(analysisRef);
}

/**
 * Delete all user data (GDPR Right to Erasure)
 */
export async function deleteUserData(userId) {
  const { deleteDoc, doc, collection, query, where, getDocs } = await import('firebase/firestore');
  
  // Delete user document
  const userRef = doc(db, 'users', userId);
  await deleteDoc(userRef);
  
  // Delete all user's analyses
  const analysesRef = collection(db, 'analyses');
  const q = query(analysesRef, where('userId', '==', userId));
  const snapshot = await getDocs(q);
  
  const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
}

/**
 * Get monthly limit
 */
export function getMonthlyLimit() {
  return FRAMEWORK_LIMITS.current;
}

export function getFrameworkLimitByType(frameworkType = 'current') {
  return getFrameworkLimit(frameworkType);
}

function frameworkStatus(framework, count) {
  const limit = FRAMEWORK_LIMITS[framework];
  return {
    hasQuota: count < limit,
    remaining: Math.max(0, limit - count),
    count,
    limit,
    framework,
  };
}

/**
 * Read this month's usage, as counted by the server (UTC months, like the server)
 */
export async function getUserQuotaStatus(userId) {
  const usageDoc = await getDoc(doc(db, 'quota_usage', userId));
  const usage = usageDoc.exists() ? usageDoc.data() : null;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const sameMonth = usage?.month === thisMonth;

  return {
    current: frameworkStatus('current', sameMonth ? usage.current || 0 : 0),
    advanced: frameworkStatus('advanced', sameMonth ? usage.advanced || 0 : 0),
  };
}
